// Per-surface graph scope — the SET of graphs a surface reads from, backed by
// GraphMultiPicker. Every surface that offers graph selection persists its own
// last-used set under its own key (the pattern the Query page established), so
// switching tabs never silently re-scopes another surface.

import { useCallback, useState } from 'react';
import { ALL_GRAPHS } from '@/components/GraphSelector';

export function useGraphScope(
  storageKey: string,
  coldStart: string[] = [],
): [string[], (next: string[]) => void] {
  const [graphs, setGraphs] = useState<string[]>(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      // A stored empty array is a real choice ("no graph"); only a MISSING key
      // falls back to the surface's cold-start default.
      if (raw !== null) {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) {
          return parsed.filter((g): g is string => typeof g === 'string');
        }
      }
    } catch {
      // Storage unavailable or the stored value is not JSON — cold-start.
    }
    return coldStart;
  });

  const pick = useCallback(
    (next: string[]) => {
      setGraphs(next);
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Storage unavailable (private mode) — selection just won't persist.
      }
    },
    [storageKey],
  );

  return [graphs, pick];
}

/** True when the scope is the all-graphs union pseudo-graph. */
export function isUnionScope(graphs: string[]): boolean {
  return graphs.includes(ALL_GRAPHS);
}

/**
 * The graph arguments a fan-out read should issue, one request per entry.
 * The union is a single request against the pseudo-graph (the backend reads
 * every graph for it), never one request per named graph.
 */
export function scopeRequests(graphs: string[]): string[] {
  return isUnionScope(graphs) ? [ALL_GRAPHS] : graphs;
}

/**
 * Narrow VDB search hits to a graph scope. `/api/v1/vdb/search` has no graph
 * parameter, so the scope is applied to each hit's own graph attribution. A
 * union (or empty) scope keeps everything; a hit the index could not attribute
 * to a graph is KEPT rather than hidden, matching the backend's own rule that
 * an unresolvable attribution must not silently remove a real result.
 */
export function hitsWithinScope<T extends { graph: string | null }>(
  hits: T[],
  graphs: string[],
): T[] {
  if (graphs.length === 0 || isUnionScope(graphs)) return hits;
  return hits.filter((h) => h.graph === null || graphs.includes(h.graph));
}

/** Whether a search must over-fetch so the client-side scope filter above
 *  still leaves a full page of hits. */
export function scopeNarrowsHits(graphs: string[]): boolean {
  return graphs.length > 0 && !isUnionScope(graphs);
}

/**
 * Stable key for a scope, for the few places that must reduce the set to one
 * opaque string (the saved viz layout is stored per user+graph). A one-graph
 * scope keys as that graph, so arrangements saved before multiselect still
 * restore.
 */
export function scopeKey(graphs: string[]): string {
  if (isUnionScope(graphs)) return ALL_GRAPHS;
  if (graphs.length === 0) return '';
  if (graphs.length === 1) return graphs[0]!;
  return [...graphs].sort().join(' ');
}
